import { useCallback } from "react";
import mapboxgl, { Marker } from "mapbox-gl";
import { CustomMarker } from "../types/types";
import { useAppState } from "../core/appState/UseAppStateZustand";
import { useMapFunctions } from "./UseMapFunctions";
import { writeToGA4, writeToLog } from "../const";

interface UsePopUpProps {
  isSmallScreen: boolean;
  popupContainerRef: React.MutableRefObject<HTMLDivElement | null>;
  popup: mapboxgl.Popup;
  mapContainer: React.MutableRefObject<HTMLDivElement | null>;
  map: React.MutableRefObject<mapboxgl.Map | null>;
}

export function usePopUp({ isSmallScreen, map, popup }: UsePopUpProps) {
  const {
    setZoom,
    setSelectedFeature,
    setFlyingTo,
    searchResults,
    selectedFeature,
    markersRef,
  } = useAppState.getState();

  const { searchResultsToGeoJson, setMarkerVisibility } = useMapFunctions();

  const closePopUp = useCallback(() => {
    const marker = (markersRef.current ?? []).find(
      (m: any) => m.id === selectedFeature?.properties?.id
    )?.marker;

    if (marker) {
      //@ts-ignore
      marker.setPopup(); //FIXME
    }
        // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFeature]);

  const handleClosePopup = useCallback(() => {
    const { newMarker, setNewMarker } = useAppState.getState();

    if (newMarker) {
      newMarker.getPopup()?.off("close", handleClosePopup);
      newMarker.remove();

      setNewMarker(null);
    }
  }, []);

  const doNothingOnClose = () => {};

  const openPopUp = useCallback(
    (
      popupContainerRef: React.MutableRefObject<HTMLDivElement | null>,
      locationId: number,
      feature?: any,
      localMarkers?: CustomMarker[],
      localMarker?: Marker
    ) => {
      writeToLog([
        "[UsePopUp] running openPopUp",
        locationId,
        feature,
        localMarker,
        localMarkers,
        popupContainerRef.current,
      ]);
      if (
        selectedFeature &&
        selectedFeature?.properties?.id !== feature?.properties?.id
      )
      {
        closePopUp();
        popup.removeClassName("new-location-popup")
      }

      if (!feature) {
        const selectedLocation = [...searchResults].find(
          (s) => s.locationId === locationId
        );

        if (selectedLocation) {
          //@ts-ignore
          feature = searchResultsToGeoJson([selectedLocation]).features[0]; //FIXME
        }
      }

      writeToLog(["[UsePopUp] [openPopUp] setting selected feature", feature]);

      setSelectedFeature(feature);
      const { isRunningInWebView } = useAppState.getState();

      writeToGA4(
        isRunningInWebView ? "mobile_app" : "web_app",
        "location_click",
        { locationName: feature?.properties?.name }
      );

      const { markersRef } = useAppState.getState();

      if (!localMarkers && !markersRef?.current) {
        writeToLog(["[UsePopUp] [openPopUp] no markers, exiting"]);
        return;
      }

      const marker =
        localMarker ??
        (localMarkers ?? markersRef.current)?.find((m: any) => m.id === locationId)
          ?.marker;

      writeToLog([
        "[UsePopUp] [openPopUp] marker",
        marker,
        localMarkers,
        locationId,
        localMarker,
        markersRef.current,
      ]);

      if (!marker) return;

      if (map.current) {
        setMarkerVisibility({ marker: marker }, true);
        //@ts-ignore
        marker.setPopup(popup); //FIXME

        popup.setLngLat(feature.geometry.coordinates).addTo(map.current);
        popup.setDOMContent(popupContainerRef.current as Node);

        if (!localMarker) {
          const offset = isSmallScreen
            ? new mapboxgl.Point(0, -window.innerHeight / 8)
            : new mapboxgl.Point(window.innerWidth / 4, 0);

          setFlyingTo(true);

          const z = map.current.getZoom();

          map.current.flyTo({
            center: feature.geometry.coordinates,
            offset: offset,
            zoom: isSmallScreen ? z : z < 10 ? 10 : z,
            essential: true,
            speed: 2,
          });

          setZoom(z < 10 ? 10 : z);

          const handleMoveEnd = () => {
            if (!map?.current) return;
            setTimeout(() => {
              setFlyingTo(false);
            }, 400);

            try {
              popup.off("close", handleClosePopup);
            } catch (e) {}

            try {
              popup.off("close", doNothingOnClose);
            } catch (e) {}

            if (locationId === -1) {
              popup.on("close", handleClosePopup);
            } else {
              popup.on("close", doNothingOnClose);
            }

            map.current?.off("moveend", handleMoveEnd);
          };

          map.current.on("moveend", handleMoveEnd);
          return;
        } else {
          try {
            popup.off("close", handleClosePopup);
          } catch (e) {}

          try {
            popup.off("close", doNothingOnClose);
          } catch (e) {}

          if (locationId === -1) {
            popup.on("close", handleClosePopup);
          } else {
            popup.on("close", doNothingOnClose);
          }
        }
      }
    },
    [
      searchResults,
      selectedFeature,
      closePopUp,
      popup,
      isSmallScreen,
      map,
      setFlyingTo,
      setZoom,
      setSelectedFeature,
      searchResultsToGeoJson,
      setMarkerVisibility,
      handleClosePopup,
    ]
  );

  return { openPopUp, closePopUp };
}
